import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { safeReturnPath } from './lib/safePath';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 30 seconds — fresh enough for a medical workflow, not hammering the server.
      // useCurrentUser overrides this to 0 (user/practice data must always be fresh).
      // Audit queries override refetchOnWindowFocus: false (large datasets, no need).
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const onRedirectCallback = (appState: any) => {
  // After Auth0 redirects back, restore the intended destination. On mobile the Auth0 +
  // device-verification flow is a multi-hop redirect that can DROP the per-transaction appState,
  // so fall back to the DURABLE previsit_return_to key (set by ProtectedRoute, kept by LoginPage)
  // before defaulting to /dashboard — this lands a /doctor-review/:taskId email link on the right
  // page even when appState is lost. Clear the key after consuming it.
  let returnTo = appState?.returnTo as string | undefined;
  if (!returnTo) {
    try { returnTo = sessionStorage.getItem('previsit_return_to') || undefined; } catch { /* ignore */ }
  }
  try { sessionStorage.removeItem('previsit_return_to'); } catch { /* ignore */ }
  // safeReturnPath rejects off-origin / protocol-relative values (open-redirect) AND keeps
  // replaceState from throwing a cross-origin SecurityError that would break the post-login landing.
  window.history.replaceState({}, document.title, safeReturnPath(returnTo));
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <Auth0Provider
    domain={process.env.REACT_APP_AUTH0_DOMAIN!}
    clientId={process.env.REACT_APP_AUTH0_CLIENT_ID!}
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      scope: 'openid profile email',
    }}
    onRedirectCallback={onRedirectCallback}
    cacheLocation="localstorage"
    useRefreshTokens={false}
  >
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </Auth0Provider>
);
